import * as React from "react";
// Constants
import { excelLogoIconProps, oneNoteLogoIconProps, pdfIconProps, powerPointLogoIconProps, projectLogoIconProps, visioLogoIconProps, wordLogoIconProps } from "../../../icons/icons.styles";
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { Breadcrumb, buildColumns, CheckboxVisibility, DefaultButton, DetailsHeader, DetailsList, DetailsRow, DialogFooter, Icon, IconButton, Label, PrimaryButton, SelectionMode, Spinner, SpinnerSize } from "@fluentui/react";
import { SupportedReferenceTypes } from "@ms/uno-models/lib/local/client/taskAttachment/ITaskAttachmentType";
// Styles
import { getClassNames, getStyles } from "./OneDrivePicker.styles";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import filter from "lodash/filter";
import findIndex from "lodash/findIndex";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import slice from "lodash/slice";
import { useState } from "react";
const ROOT_FOLDER = "root";
export function OneDrivePicker({ isDriveItemAnAttachment, onDismiss, oneDrivePickerStrings, showErrorLoadingFiles, detailsListProps, groupDriveItemsByFolder, icons, onFolderDrillDown, onRetry, showRetryOnError, alertUserAddAttachmentError, addDriveItemAsAttachment }) {
    const driveItemIconSize = 20;
    const maxDisplayedFolderItems = 5;
    const columnName = "name";
    const columnFileType = "fileType";
    const columnModifiedDate = "modifiedDate";
    /** Used to save the last folder accessed */ /**
     * Initializes a new instance of OneDrivePicker
     * @param props For the OneDrivePicker
     */ let groupDocuments;
    if (groupDriveItemsByFolder != null) {
        groupDocuments = groupDriveItemsByFolder[ROOT_FOLDER];
    }
    // State for the component
    const [selectedDriveItem, setSelectedDriveItem] = useState(null);
    const [alertAddedForAttachment, setAlertAddedForAttachmnet] = useState(null);
    const [columns, setColumns] = useState(buildColumnsFromDriveItems(groupDocuments)); // Build columns from root folder
    const [breadcrumbItems, setBreadCrumbItems] = useState([]);
    const [initialFocusedIndex] = useState(undefined);
    /** Used to save the last folder accessed */ const [prevFolderKey, setPrevFolderKey] = useState("");
    const styles = getStyles();
    const classNames = getClassNames(getStyles(styles));
    const dialogHeaderText = breadcrumbItems == null || breadcrumbItems.length === 0 ? oneDrivePickerStrings.documents : null;
    let attachmentError = null;
    if (selectedDriveItem != null) {
        attachmentError = isDriveItemAnAttachment(selectedDriveItem) ? oneDrivePickerStrings.existingFileError : null;
    }
    /**
     * Sort the columns in descending order
     * @param columnCriteria column key to sort by
     */ const columnComparatorDescending = (columnCriteria)=>(driveItem1, driveItem2)=>driveItem1[columnCriteria] === driveItem2[columnCriteria] ? 0 : driveItem1[columnCriteria] > driveItem2[columnCriteria] ? -1 : 1;
    /**
     * Sort the columns in ascending order
     * @param columnCriteria column key to sort by
     */ const columnComparatorAscending = (columnCriteria)=>(driveItem1, driveItem2)=>driveItem1[columnCriteria] === driveItem2[columnCriteria] ? 0 : driveItem1[columnCriteria] < driveItem2[columnCriteria] ? -1 : 1;
    /**
     * Render the drive items
     */ function renderDriveItems() {
        const classNames = getClassNames(getStyles(styles));
        // Try to get folder contents for selected item
        let folderContents = null;
        if (groupDriveItemsByFolder != null) {
            folderContents = groupDriveItemsByFolder[getFolderKey()];
        }
        // const columns = columns || buildColumnsFromDriveItems(folderContents);
        if (folderContents) {
            if (folderContents.length === 0) {
                return /*#__PURE__*/ React.createElement("div", {
                    className: classNames.emptyContainer
                }, /*#__PURE__*/ React.createElement("div", {
                    className: classNames.emptyFolder
                }, /*#__PURE__*/ React.createElement(Icon, {
                    styles: styles.subComponentStyles.emptyFolderIcon,
                    iconName: "FabricOpenFolderHorizontal",
                    ...icons?.openFolder
                }), /*#__PURE__*/ React.createElement(Label, {
                    styles: styles.subComponentStyles.emptyFolderText,
                    role: "alert"
                }, oneDrivePickerStrings.emptyFolder)));
            } else {
                folderContents = sortFolderContentsByColumnCriteria(folderContents, columns ? columns : buildColumnsFromDriveItems(folderContents));
                return /*#__PURE__*/ React.createElement("div", {
                    className: classNames.listContainer,
                    "data-is-scrollable": "true"
                }, /*#__PURE__*/ React.createElement(DetailsList, {
                    ...detailsListProps,
                    ariaLabel: oneDrivePickerStrings.sharePointFileListLabel,
                    checkboxVisibility: CheckboxVisibility.hidden,
                    columns: columns ? columns : buildColumnsFromDriveItems(folderContents ? folderContents : []),
                    initialFocusedIndex: initialFocusedIndex,
                    items: folderContents ? folderContents : [],
                    selectionMode: SelectionMode.single,
                    getKey: getKey,
                    onActiveItemChanged: onItemSelection,
                    onDidUpdate: onDetailsListUpdated,
                    onColumnHeaderClick: onColumnClick,
                    onRenderItemColumn: renderItemColumn,
                    onRenderRow: onRenderRow,
                    onRenderDetailsHeader: onRenderDetailsHeader,
                    styles: styles.subComponentStyles.listDetails
                }));
            }
        } else {
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.emptyContainer
            }, /*#__PURE__*/ React.createElement(Spinner, {
                ariaLabel: oneDrivePickerStrings.loadingFolderAriaLabel,
                ariaLive: "assertive",
                label: oneDrivePickerStrings.loadingFolderLabel,
                size: SpinnerSize.large
            }));
        }
    }
    /**
     * Updated selected item on selection
     * @param item User selected drive item
     */ function onItemSelection(item) {
        const selectedDriveItem = item.childCount > -1 ? null : item;
        let driveItemWithAlert;
        if (selectedDriveItem != null && isDriveItemAnAttachment(selectedDriveItem) && selectedDriveItem !== alertAddedForAttachment) {
            alertUserAddAttachmentError();
            driveItemWithAlert = selectedDriveItem;
        }
        setSelectedDriveItem(selectedDriveItem);
        setAlertAddedForAttachmnet(driveItemWithAlert ? driveItemWithAlert : null);
    }
    /**
     * Sort the folder content by the column criteria
     * @param folderContents Folder drive items
     * @param columns Drive picker columns
     */ function sortFolderContentsByColumnCriteria(folderContents, columns) {
        if (folderContents == null) {
            return null;
        }
        const sortedFolderContents = folderContents;
        let columnCriteria = "";
        let isSortedDescending = false;
        columns?.forEach((column)=>{
            if (column.isSorted) {
                columnCriteria = column.key;
                isSortedDescending = !!column.isSortedDescending;
            }
        });
        if (columnCriteria !== "") {
            if (isSortedDescending) {
                sortedFolderContents.sort(columnComparatorDescending(columnCriteria));
            } else {
                sortedFolderContents.sort(columnComparatorAscending(columnCriteria));
            }
        }
        return sortedFolderContents;
    }
    /**
     * Render the error loading container
     */ function renderLoadingError() {
        const classNames = getClassNames(getStyles(styles));
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.errorContainer
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames.errorFolder
        }, /*#__PURE__*/ React.createElement(Icon, {
            styles: styles.subComponentStyles.errorFolderIcon,
            iconName: "FabricOpenFolderHorizontal",
            ...icons?.openFolder
        }), /*#__PURE__*/ React.createElement("div", {
            className: classNames.errorTitle,
            role: "alert"
        }, oneDrivePickerStrings.loadingErrorTitle), /*#__PURE__*/ React.createElement("div", {
            className: classNames.errorMessage,
            role: "alert"
        }, oneDrivePickerStrings.loadingErrorMessage), showRetryOnError && onRetry && /*#__PURE__*/ React.createElement(DefaultButton, {
            className: "retry",
            onClick: onRetry
        }, oneDrivePickerStrings.retry)));
    }
    /**
     * Get the key for the folder to store/retrieve contents
     */ function getFolderKey() {
        return breadcrumbItems == null || breadcrumbItems.length === 0 ? ROOT_FOLDER : breadcrumbItems[breadcrumbItems.length - 1].key;
    }
    /**
     * Navigate back to selected parent folder
     * @param ev Event fired when user clicks on breadcrumb
     * @param item Selected breadcrumbItem
     */ function rollUpFolder(ev, item) {
        let folderContents;
        if (groupDriveItemsByFolder != null) {
            folderContents = groupDriveItemsByFolder[item.key];
            setColumns(buildColumnsFromDriveItems(folderContents));
        }
        const index = findIndex(breadcrumbItems, (breadcrumbItem)=>breadcrumbItem.key === item.key);
        setBreadCrumbItems(index === 0 ? [] : slice(breadcrumbItems, 0, index + 1));
        setSelectedDriveItem(null);
    }
    /**
     * Save the selected attachment on task
     */ function addDriveItemAsAttachmentLocal() {
        if (!selectedDriveItem) {
            return;
        }
        // Already exists, don't add again
        if (isDriveItemAnAttachment(selectedDriveItem)) {
            return;
        }
        addDriveItemAsAttachment(selectedDriveItem);
        onDismiss();
    }
    /**
     * Handler for sorting the columns
     * @param [ev] Event fired when user clicks on column
     * @param [column] Column user clicked on
     */ function onColumnClick(ev, column) {
        if (!column) {
            return;
        }
        let sortedItems;
        if (groupDriveItemsByFolder != null) {
            sortedItems = groupDriveItemsByFolder[getFolderKey()];
        }
        setColumns(buildColumnsFromDriveItems(sortedItems));
        let isSortedDescending = column.isSortedDescending;
        // If we've sorted this column, flip it.
        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }
        // Reset the items and columns to match the state.
        setColumns(columns?.map((col)=>{
            col.isSorted = col.key === column.key;
            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        }));
    }
    /**
     * Delegate to overwrite details header styles
     * @param detailsHeaderProps Details header props
     */ function onRenderDetailsHeader(detailsHeaderProps) {
        const styles = getStyles();
        //TODO: DetailsHeader icons are not replaceable for svg which means loop cannot show the default fabric icons, consider rewrite this
        return /*#__PURE__*/ React.createElement(DetailsHeader, {
            ...detailsHeaderProps,
            styles: styles.subComponentStyles.listDetailsHeader
        });
    }
    /**
     * Delegate to overwrite CSS properties of row
     * @param detailsRowProps Details row props
     */ function onRenderRow(detailsRowProps) {
        const styles = getStyles();
        const onKeyUp = (event)=>{
            if (event.keyCode === KeyCode.ENTER) {
                detailsRowProps.item.childCount > -1 ? drillDownFolder(detailsRowProps.item) : addDriveItemAsAttachmentLocal();
            }
        };
        return /*#__PURE__*/ React.createElement("div", {
            onKeyUp: onKeyUp
        }, /*#__PURE__*/ React.createElement(DetailsRow, {
            ...detailsRowProps,
            styles: styles.subComponentStyles.listDetailsRow
        }));
    }
    /**
     * Delegate to overwrite CSS properties of column item
     * @param item Drive item to be rendered
     * @param index Column index
     * @param column The column containing the drive item
     */ function renderItemColumn(item, index, column) {
        const fieldContent = column.fieldName && item[column.fieldName];
        switch(column.key){
            case columnName:
                if (item.childCount > -1) {
                    return /*#__PURE__*/ React.createElement("span", {
                        className: "folderItemText",
                        onClick: ()=>drillDownFolder(item)
                    }, fieldContent);
                }
                return /*#__PURE__*/ React.createElement("span", {
                    className: "itemText"
                }, fieldContent);
            case columnModifiedDate:
                return /*#__PURE__*/ React.createElement("span", {
                    className: "itemText"
                }, item.modifiedDate.format("LL"));
            case columnFileType:
                return /*#__PURE__*/ React.createElement(IconButton, {
                    className: "previewIcon",
                    iconProps: getIconPropsForDriveItem(item),
                    ariaLabel: getIconAriaLabel(item)
                });
            default:
                return /*#__PURE__*/ React.createElement("span", {
                    className: "itemText"
                }, fieldContent);
        }
    }
    /**
     * Get the icon arialabel for the attachment type.
     * @param item The drive item we want the icon class for
     */ function getIconAriaLabel(item) {
        if (item.childCount > -1) {
            return "FabricFolder";
        }
        switch(item.fileType){
            case SupportedReferenceTypes.Excel:
                return getAriaLabelForIcon(excelLogoIconProps["aria-label"] ? excelLogoIconProps["aria-label"] : "", 4);
            case SupportedReferenceTypes.OneNote:
                return getAriaLabelForIcon(oneNoteLogoIconProps["aria-label"] ? oneNoteLogoIconProps["aria-label"] : "", 4);
            case SupportedReferenceTypes.Pdf:
                // PDF icon type is 'plannerPdf' with no logo. No need to trim.
                return getAriaLabelForIcon(pdfIconProps["aria-label"] ? pdfIconProps["aria-label"] : "", 0);
            case SupportedReferenceTypes.PowerPoint:
                return getAriaLabelForIcon(powerPointLogoIconProps["aria-label"] ? powerPointLogoIconProps["aria-label"] : "", 4);
            case SupportedReferenceTypes.Project:
                return getAriaLabelForIcon(projectLogoIconProps["aria-label"] ? projectLogoIconProps["aria-label"] : "", 4);
            case SupportedReferenceTypes.Visio:
                return getAriaLabelForIcon(visioLogoIconProps["aria-label"] ? visioLogoIconProps["aria-label"] : "", 4);
            case SupportedReferenceTypes.Word:
                return getAriaLabelForIcon(wordLogoIconProps["aria-label"] ? wordLogoIconProps["aria-label"] : "", 4);
            case SupportedReferenceTypes.Other:
                return oneDrivePickerStrings.image;
            default:
                return "Document";
        }
    }
    /**
     * Get the icon for the attachment type. Icon name strings should match up with https://developer.microsoft.com/en-us/fluentui#/styles/web/icons
     * @param item The drive item we want the icon class for
     * @param returnAriaLabel If true, trims the icon name to get just the file type and adds it to the fileName string passed as a prop.
     */ function getIconPropsForDriveItem(item) {
        if (item.childCount > -1) {
            return icons?.folder ?? {
                iconName: "FabricFolder"
            };
        }
        switch(item.fileType){
            case SupportedReferenceTypes.Excel:
                return icons?.excel ?? {
                    iconName: "ExcelLogo"
                };
            case SupportedReferenceTypes.OneNote:
                return icons?.onenote ?? {
                    iconName: "OneNoteLogo"
                };
            case SupportedReferenceTypes.Pdf:
                return icons?.pdf ?? {
                    iconName: "PDF"
                };
            case SupportedReferenceTypes.PowerPoint:
                return icons?.powerpoint ?? {
                    iconName: "PowerPointLogo"
                };
            case SupportedReferenceTypes.Project:
                return icons?.project ?? {
                    iconName: "ProjectLogo32"
                };
            case SupportedReferenceTypes.Visio:
                return icons?.visio ?? {
                    iconName: "VisioLogo"
                };
            case SupportedReferenceTypes.Word:
                return icons?.word ?? {
                    iconName: "WordLogo"
                };
            case SupportedReferenceTypes.Other:
                return icons?.photo ?? {
                    iconName: "Photo2"
                };
            default:
                return icons?.document ?? {
                    iconName: "Document"
                };
        }
    }
    /**
     * Called when user clicks on folder
     * @param selectedFolder Drive item selected by user
     */ function drillDownFolder(selectedFolder) {
        onFolderDrillDown(selectedFolder.id);
        const folderContents = groupDriveItemsByFolder[selectedFolder.id];
        const newBreadcrumbItems = breadcrumbItems ? breadcrumbItems : [];
        if (newBreadcrumbItems.length === 0) {
            newBreadcrumbItems.push({
                text: oneDrivePickerStrings.documents,
                key: ROOT_FOLDER,
                onClick: rollUpFolder
            });
        }
        newBreadcrumbItems.push({
            text: selectedFolder.name,
            key: selectedFolder.id,
            onClick: rollUpFolder
        });
        setColumns(buildColumnsFromDriveItems(folderContents));
        setBreadCrumbItems(newBreadcrumbItems);
    }
    /**
     * Gets the key for a details row
     * @param item The drive item to get the key from
     */ function getKey(item) {
        return item.id;
    }
    /** Callback when details list is updated */ function onDetailsListUpdated(detailsList) {
        // Ideally the following piece of code should not have a dedicated state variable to store the previous folder key.
        // The re-focussing of the first item in the details list rows should have depended on existing state variable 'breadcrumbItems'
        // using useEffect hook, but in the current scenario if we try to use useEffect hook based on 'breadcrumbItems' state variable,
        // we do not have 'detailsList' object to re-direct the focus which is only available in the present method, which needs us to store
        // the previous folder key in a state variable to compare it to current folder key which is dependent on 'breadcrumbItems'.
        if (prevFolderKey !== getFolderKey()) {
            // Update previous folder
            setPrevFolderKey(getFolderKey());
            // focus on first item in details list rows
            detailsList?.focusIndex(0);
        }
    }
    /**
     * Trims the icon name to get just the file type and adds it to the fileName string passed as a prop.
     * For ex: 'wordlogo' => 'word file.', 'projectlogo32' => 'project file.', 'pdf' => 'pdf file.'
     * @param iconName Supported SharePoint file types
     * @param trimBy A number that indicates how many character to trim at the end of the iconName
     */ function getAriaLabelForIcon(iconName, trimBy) {
        return StringHelper.format(oneDrivePickerStrings.fileName, iconName.substring(0, iconName.length - trimBy));
    }
    /**
     * Builds columns from the provided data
     * @param items Items to build the columns
     */ function buildColumnsFromDriveItems(items) {
        if (items != null && !isEmpty(items)) {
            const columns = buildColumns(items);
            const columnsToDisplay = filter(columns, (column)=>column.name === columnName || column.name === columnModifiedDate);
            forEach(columnsToDisplay, (column)=>{
                column.className = "itemText";
                switch(column.name){
                    case columnName:
                        column.name = oneDrivePickerStrings.columnName;
                        column.ariaLabel = StringHelper.format(oneDrivePickerStrings.columnHeaderAriaLabel, oneDrivePickerStrings.columnName);
                        column.sortAscendingAriaLabel = oneDrivePickerStrings.columnSortAscendingAriaLabel;
                        column.sortDescendingAriaLabel = oneDrivePickerStrings.columnSortDescendingAriaLabel;
                        break;
                    case columnModifiedDate:
                        column.name = oneDrivePickerStrings.columnModifiedDate;
                        column.ariaLabel = StringHelper.format(oneDrivePickerStrings.columnHeaderAriaLabel, oneDrivePickerStrings.columnModifiedDate);
                        column.sortAscendingAriaLabel = oneDrivePickerStrings.columnSortAscendingAriaLabel;
                        column.sortDescendingAriaLabel = oneDrivePickerStrings.columnSortDescendingAriaLabel;
                        break;
                    default:
                        break;
                }
            });
            const fileTypeColumn = {
                key: "fileType",
                ariaLabel: StringHelper.format(oneDrivePickerStrings.columnHeaderAriaLabel, oneDrivePickerStrings.columnFileType),
                sortAscendingAriaLabel: oneDrivePickerStrings.columnSortAscendingAriaLabel,
                sortDescendingAriaLabel: oneDrivePickerStrings.columnSortDescendingAriaLabel,
                className: "previewIcon",
                fieldName: "fileType",
                iconName: "Page",
                isIconOnly: true,
                isCollapsible: true,
                isGrouped: false,
                isMultiline: false,
                isRowHeader: false,
                isSorted: false,
                isSortedDescending: false,
                maxWidth: driveItemIconSize,
                minWidth: 100,
                name: oneDrivePickerStrings.columnFileType
            };
            columnsToDisplay.unshift(fileTypeColumn);
            return columnsToDisplay;
        }
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, dialogHeaderText && /*#__PURE__*/ React.createElement("div", {
        className: classNames.dialogHeader
    }, /*#__PURE__*/ React.createElement(Label, {
        styles: styles.subComponentStyles.dialogHeaderText
    }, dialogHeaderText)), /*#__PURE__*/ React.createElement(Breadcrumb, {
        styles: styles.subComponentStyles.breadcrumb,
        items: breadcrumbItems ? breadcrumbItems : [],
        maxDisplayedItems: maxDisplayedFolderItems
    }), showErrorLoadingFiles ? renderLoadingError() : renderDriveItems(), /*#__PURE__*/ React.createElement(Label, {
        styles: styles.subComponentStyles.attachmentError
    }, attachmentError), /*#__PURE__*/ React.createElement(DialogFooter, {
        styles: styles.subComponentStyles.dialogFooter
    }, /*#__PURE__*/ React.createElement(PrimaryButton, {
        className: "saveButton",
        onClick: addDriveItemAsAttachmentLocal,
        disabled: selectedDriveItem == null || attachmentError != null || showErrorLoadingFiles
    }, oneDrivePickerStrings.save), /*#__PURE__*/ React.createElement(DefaultButton, {
        className: "cancel",
        onClick: onDismiss
    }, oneDrivePickerStrings.cancel)));
}
